import { ActionIcon, Box, createStyles, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { find, flatten, forEach, isArray, isFunction, map } from 'lodash/fp';
import React, { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { HubType } from '@portals/types';

import { useAppConfig } from '../../../../../context';
import { useCommonConfig } from '../../../../../hooks/portal-config';
import { useHasSupportSeat } from '../../../../../hooks/support-seats';
import { usePermissionAccess } from '../../../../permission-access/use-permission-access';
import { HUBS_ROUTES_GROUPS } from '../../hubs-sidebar/hubs-sidebar.constants';

interface BaseHubMenuItemProps {
  hubType: HubType;
  activeHubType: HubType;
  setActiveHubType: Dispatch<SetStateAction<HubType>>;
}

type HubMenuItemProps = BaseHubMenuItemProps &
  (
    | { label: string; icon?: undefined }
    | { icon: ReactNode; label?: undefined }
  );

export function HubMenuItem({
  activeHubType,
  setActiveHubType,
  hubType,
  icon,
  label,
}: HubMenuItemProps) {
  const { routes } = useAppConfig();
  const { cx, classes } = useStyles();
  const navigate = useNavigate();
  const configuration = useCommonConfig();
  const hasSupportSeat = useHasSupportSeat();
  const { isAdmin, canView, canEdit } = usePermissionAccess();

  const allRoutes = useMemo(
    () => flatten(map(({ childRoutes }) => childRoutes, routes.dashboard)),
    [routes.dashboard]
  );

  const onHubClick = (hubType: HubType) => {
    if (activeHubType === hubType) return;

    const hubRoutes = HUBS_ROUTES_GROUPS?.[hubType];
    let hubRouteToNavigate: string | undefined;

    forEach((hubRoute: string) => {
      const originalRoute = find({ id: hubRoute }, allRoutes);

      if (!originalRoute) return;

      const isRouteAccessible =
        originalRoute?.canAccessRoute?.({
          configuration: configuration.data,
          hasSupportSeat,
          isAdmin,
          canView,
          canEdit,
        }) ?? true;

      if (!isRouteAccessible) return;

      if (isArray(originalRoute?.path)) {
        hubRouteToNavigate = originalRoute?.path[0];
      } else if (isFunction(originalRoute?.path)) {
        hubRouteToNavigate = originalRoute?.path?.({
          configuration: configuration.data,
          isAdmin,
        })?.[0];
      } else {
        hubRouteToNavigate = originalRoute?.path;
      }

      if (hubRouteToNavigate) return false;
    }, hubRoutes);

    if (hubRouteToNavigate) {
      navigate(hubRouteToNavigate.replace('/*', ''));
      setActiveHubType(hubType);
    }
  };

  return (
    <Box
      className={cx(classes.hubSectionWrapper, {
        active: activeHubType === hubType,
      })}
      px="sm"
      onClick={() => onHubClick(hubType)}
    >
      {icon ? (
        <ActionIcon
          color={activeHubType === hubType ? 'gray.1' : 'blue_gray.2'}
          variant="transparent"
        >
          {icon}
        </ActionIcon>
      ) : (
        <Text
          size="sm"
          color={activeHubType === hubType ? 'white' : 'blue_gray.2'}
        >
          {label}
        </Text>
      )}

      {activeHubType === hubType ? (
        <motion.div
          className={classes.activeHubIndicator}
          layoutId="active-hub"
        />
      ) : null}
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  hubSectionWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.other.navbarHeight,
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',

    '&.active, &:hover': {
      backgroundColor: '#3C3E5E',
    },
  },
  activeHubIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: theme.colors.indigo_accent[2],
    borderRadius: `${theme.radius.sm} ${theme.radius.sm} 0 0`,
    zIndex: 1,
  },
}));
